import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { Student, StudentStatus } from '@/types/domain';
import { formatDate, orDash } from '@/utils/format';
import { StudentFormModal, toPayload } from './StudentForm';

export function StudentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('lastName,asc');

  const [statusFilter, setStatusFilter] = useState<StudentStatus | ''>('');
  const [formOpen, setFormOpen] = useState(false);

  const params = { ...table.params, ...(statusFilter !== '' ? { status: statusFilter } : {}) };

  const query = useQuery({
    queryKey: queryKeys.students.list(params),
    queryFn: () => studentsApi.list(params),
  });

  const create = useMutation({
    mutationFn: studentsApi.create,
    onSuccess: (student) => {
      toast.success(`${student.fullName} registered`);
      setFormOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
    },
  });

  const columns: Column<Student>[] = [
    {
      key: 'number',
      header: 'Number',
      sortKey: 'studentNumber',
      render: (student) => <span className="font-mono text-xs text-muted">{student.studentNumber}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sortKey: 'lastName',
      render: (student) => <span className="font-medium text-ink">{student.fullName}</span>,
    },
    {
      // Derived from the active enrolment in the current academic year — there
      // is no grade column on the student record.
      key: 'placement',
      header: 'Current class',
      render: (student) =>
        student.currentPlacement === undefined ? (
          <span className="text-subtle">Not enrolled</span>
        ) : (
          <span>
            {student.currentPlacement.gradeName}
            {student.currentPlacement.sectionName !== undefined && ` · ${student.currentPlacement.sectionName}`}
            {student.currentPlacement.rollNumber !== undefined && (
              <span className="ml-1 text-xs text-subtle">#{student.currentPlacement.rollNumber}</span>
            )}
          </span>
        ),
    },
    {
      key: 'admitted',
      header: 'Admitted',
      sortKey: 'admissionDate',
      render: (student) => formatDate(student.admissionDate),
    },
    { key: 'email', header: 'E-mail', render: (student) => orDash(student.email) },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (student) => <StatusBadge status={student.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Students"
        description="Student records. A student's class comes from their enrolment for the current academic year."
        action={
          can(PERMISSIONS.STUDENT_CREATE) ? (
            <Button onClick={() => setFormOpen(true)}>Register student</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by name, number or e-mail…',
        }}
        filters={
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
              { value: 'GRADUATED', label: 'Graduated' },
              { value: 'TRANSFERRED', label: 'Transferred' },
              { value: 'WITHDRAWN', label: 'Withdrawn' },
            ]}
          />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(student) => student.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(student) => navigate(ROUTES.studentDetail(student.id))}
          emptyTitle="No students found"
          emptyDescription={
            table.searchInput === '' && statusFilter === ''
              ? 'Register your first student to get started.'
              : 'No students match the current filters.'
          }
        />
      </ListPage>

      <StudentFormModal
        open={formOpen}
        student={null}
        saving={create.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={(values, setError) =>
          create.mutate(toPayload(values), { onError: (error) => handleApiError(error, setError) })
        }
      />
    </>
  );
}
